import { field } from '../fields'
import { useState } from 'react'
import Modal from './Modal'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { clockTime, meridiem } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import {
  ABSENCE_REASONS,
  byDancer,
  excusable,
  shortClassName,
  reportBulkAbsence,
  uploadAbsenceDocument,
  type AbsenceReason,
  type AffectedSession,
  type PortalHome,
} from '../api/portal'
/**
 * Telling the studio a child will be away, once (D-177).
 *
 * **A child off sick for two days is one fact about that child.** Reporting it
 * per session made a parent state it seven times — three on a busy Tuesday for
 * one dancer alone, each with its own reason. She gives dates and a reason here;
 * the app works out which classes that means.
 *
 * **The confirmation step is the point, not politeness.** She picks dates; the
 * server resolves the sessions and shows them before anything is written. That
 * way a solo, or a rehearsal added since she last looked, appears in front of
 * her instead of being silently excused — the same reasoning that made the
 * import preview reconcile against the studio it would produce (D-91).
 */
export default function ReportAbsence({
  dancers,
  preselected,
  startOn,
  initialNote,
  onClose,
}: {
  dancers: PortalHome['dancers']
  /** Whoever the schedule is showing, so the common case is already answered. */
  preselected: string[]
  /**
   * The day the schedule is showing, so opening this from Tuesday starts on
   * Tuesday. Reporting one class was the case the per-row link used to serve,
   * and it stays short without the clutter (D-177).
   */
  startOn?: string
  /** Her words, carried in from a conversation as the note (D-630); still hers to change. */
  initialNote?: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const [from, setFrom] = useState(startOn ?? today())
  const [to, setTo] = useState(startOn ?? today())
  const [chosen, setChosen] = useState<string[]>(preselected)
  const [reason, setReason] = useState<AbsenceReason>('Illness')
  const [note, setNote] = useState(initialNote ?? '')
  const [found, setFound] = useState<AffectedSession[] | null>(null)

  // The chosen file, and the id once it has actually been uploaded. Kept apart
  // so a retry does not upload the same note twice (D-178).
  const [note_file, setNoteFile] = useState<File | null>(null)
  const [attached, setAttached] = useState<string | null>(null)

  /*
   * Classes she will still make, unticked from the confirmation (D-181).
   *
   * Held as exclusions rather than selections so a *new* preview - different
   * dates, another child - arrives fully ticked, which is the usual case. A set
   * of selections would have to be rebuilt every time and would silently drop
   * anything the parent had not looked at yet.
   */
  const [skipped, setSkipped] = useState<string[]>([])

  const run = useMutation({
    mutationFn: async (apply: boolean) => {
      /*
       * Uploaded on submit, not on selection (D-178). Attaching it earlier
       * would leave a child's medical note in storage every time somebody
       * opened this dialog and changed their mind — and nothing would ever
       * collect it, because the retention clock is anchored to an absence that
       * was never reported.
       *
       * One note for the whole absence, stapled to each session it covers.
       */
      let documentMediaAssetId = attached

      if (apply && note_file && !documentMediaAssetId) {
        documentMediaAssetId = await uploadAbsenceDocument(note_file)
        setAttached(documentMediaAssetId)
      }

      return reportBulkAbsence({
        from,
        to,
        dancerIds: chosen,
        reason,
        note: note.trim() || null,
        documentMediaAssetId: apply ? documentMediaAssetId : null,
        // Exactly what is ticked, so what she confirmed is what happens.
        occurrenceIds: apply ? willExcuse.map((s) => s.occurrenceId) : [],
        apply,
      })
    },
    onSuccess: (result) => {
      setFound(result.sessions)

      if (result.applied) {
        // Every schedule showing those sessions is now out of date.
        queryClient.invalidateQueries({ queryKey: ['portal-dancer'] })
        onClose()
      }
    },
  })

  const toggle = (id: string) =>
    setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]))

  // Dates or dancers changed, so the confirmation on screen is about a
  // different question and must not be submittable.
  const invalidate = () => {
    setFound(null)
    setSkipped([])
  }

  const toggleSession = (s: AffectedSession) => {
    const key = s.occurrenceId + s.dancerId
    setSkipped((k) => (k.includes(key) ? k.filter((x) => x !== key) : [...k, key]))
  }

  // Short enough to scan down a column of fourteen.
  const shortDate = (iso: string) =>
    new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })

  // What is actually going to be written: found, minus what the studio has
  // already marked, minus what she has unticked (D-181).
  const willExcuse = found
    ? excusable(found).filter((s) => !skipped.includes(s.occurrenceId + s.dancerId))
    : []

  const alreadyDone = found ? found.length - excusable(found).length : 0

  return (
    /*
      <b>Escape and a backdrop tap close it</b> (D-515).

      It had neither, while <c>PrivateRequestsPanel</c> — the button standing
      immediately beside it on the same row of the parent portal — has both.
      That file's own comment named this as *"a gap there rather than a
      precedent worth copying"*, and the gap outlived the comment. Two dialogs a
      parent reaches from one row should not need learning separately.

      <b>It closes only when the press <i>and</i> the release were both on the
      dim area</b>, which needs the mousedown remembered. Testing the click
      alone is not enough and looks as though it is: a click is dispatched at
      the nearest common ancestor of its press and release, so selecting text in
      the note field and overshooting upward retargets the click to the overlay
      and would have thrown the form away — her dates, her dancers, her reason,
      her note and any doctor's note attached to it. `observed` in Edge, by a
      cold audit, against a first cut whose comment claimed exactly the
      protection it did not have.
    */
    <Modal
      onDismiss={onClose}
      label="Report an absence"
      overlayClassName="fixed inset-0 z-50 flex items-end justify-center bg-black/45 sm:items-center"
      panelClassName="max-h-[90vh] w-full max-w-[32rem] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 sm:rounded-2xl"
    >
      <>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg">Report an absence</h2>
            <p className="mt-0.5 text-[12.5px] text-ink-3">
              Tell us the days and we will work out which classes.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold text-ink-3">First day away</span>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value)
                  // A single day is the common case, so the end follows the
                  // start until she deliberately moves it.
                  if (e.target.value > to) setTo(e.target.value)
                  invalidate()
                }}
                className={field}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold text-ink-3">Last day away</span>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(e) => {
                  setTo(e.target.value)
                  invalidate()
                }}
                className={field}
              />
            </label>
          </div>

          {dancers.length > 1 && (
            <div>
              <p className="mb-1.5 text-[12px] font-semibold text-ink-3">Who is away?</p>
              <div className="flex flex-wrap gap-2">
                {dancers.map((d) => (
                  <button
                    key={d.dancerId}
                    type="button"
                    onClick={() => {
                      toggle(d.dancerId)
                      invalidate()
                    }}
                    aria-pressed={chosen.includes(d.dancerId)}
                    className={`rounded-xl border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                      chosen.includes(d.dancerId)
                        ? 'border-accent bg-accent-soft text-accent-2'
                        : 'border-line bg-surface text-ink-2 hover:border-ink-3'
                    }`}
                  >
                    {d.firstName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">Why?</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as AbsenceReason)}
              className={field}
            >
              {ABSENCE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">
              Anything the studio should know? Optional
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className={field}
            />
          </label>

          {/*
            Optional, and asked for once rather than per class (D-178). Only
            offered where it makes sense — nobody attaches a doctor's note to a
            school concert.
          */}
          {reason === 'Illness' && (
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold text-ink-3">
                Doctor&rsquo;s note? Optional
              </span>

              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => {
                  setNoteFile(e.target.files?.[0] ?? null)
                  // A different file is a different note, so anything already
                  // uploaded must not be attached instead.
                  setAttached(null)
                }}
                className="text-[12.5px] text-ink-2 file:mr-3 file:rounded-lg file:border file:border-line-2 file:bg-surface file:px-3 file:py-1.5 file:text-[12.5px] file:font-semibold file:text-ink-2"
              />

              <span className="text-[11.5px] text-ink-3">
                Kept for a year, then deleted. Only the studio can open it.
              </span>
            </label>
          )}

          {run.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(run.error as Error).message}
            </p>
          )}

          {/*
            What she is actually about to excuse. Listed rather than counted,
            because the count is not the thing she is checking — she is looking
            for a class she did not expect (D-177).
          */}
          {found && (
            <div className="rounded-xl border border-line bg-surface-2 px-3 py-2.5">
              {found.length === 0 ? (
                <p className="text-[12.5px] text-ink-3">
                  No classes on {from === to ? formatFullDate(from) : 'those days'}.
                </p>
              ) : (
                <>
                  <p className="mb-1.5 text-[12px] font-semibold">
                    {willExcuse.length}{' '}
                    {willExcuse.length === 1 ? 'class' : 'classes'} will be marked excused
                  </p>

                  {/*
                    Grouped by child and stripped back to the class's own name
                    (D-181). Fourteen full Jackrabbit names — each carrying day,
                    time, ages, size, level and teacher — is a wall nobody reads,
                    which defeats the point of asking somebody to check it.

                    Everything is ticked to begin with, because "she is off on
                    these days" is the usual case. Untick what she will still
                    make — a dancer who misses school but makes her evening solo.
                  */}
                  <div className="flex flex-col gap-2.5">
                    {byDancer(found).map((group) => (
                      <div key={group.name}>
                        <p className="text-[11px] font-bold tracking-[0.08em] text-accent uppercase">
                          {group.name}
                        </p>

                        <ul className="mt-0.5 flex flex-col">
                          {group.sessions.map((s) => (
                            <li key={`${s.occurrenceId}-${s.dancerId}`}>
                              <label
                                className={`flex items-baseline gap-2 py-0.5 text-[12px] ${
                                  s.alreadyHandled
                                    ? 'text-ink-3'
                                    : 'cursor-pointer text-ink-2'
                                }`}
                              >
                                {/* A parent ticking classes on a phone, so it
                                    matches every other box in the app (D-229). */}
                                <input
                                  type="checkbox"
                                  className="size-4 shrink-0 accent-[var(--color-accent)]"
                                  checked={!s.alreadyHandled && !skipped.includes(s.occurrenceId + s.dancerId)}
                                  disabled={s.alreadyHandled !== null}
                                  onChange={() => toggleSession(s)}
                                />

                                <span className={s.alreadyHandled ? 'line-through' : ''}>
                                  <span className="tabular">{shortDate(s.date)}</span>
                                  {s.startTime && (
                                    <>
                                      {' '}
                                      <span className="tabular">
                                        {clockTime(s.startTime)}
                                        {meridiem(s.startTime)}
                                      </span>
                                    </>
                                  )}{' '}
                                  <span className="font-semibold wrap-anywhere">
                                    {shortClassName(s.className)}
                                  </span>
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {/* Said plainly. A class quietly missing from the count is
                      how a parent stops trusting the rest of the screen. */}
                  {alreadyDone > 0 && (
                    <p className="mt-1.5 text-[11.5px] text-ink-3">
                      {alreadyDone} already marked by the studio, so {alreadyDone === 1 ? 'it is' : 'they are'}{' '}
                      left as {alreadyDone === 1 ? 'it is' : 'they are'}.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            {found === null ? (
              <button
                type="button"
                onClick={() => run.mutate(false)}
                disabled={chosen.length === 0 || run.isPending}
                className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
              >
                {run.isPending ? 'Checking...' : 'Which classes?'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => run.mutate(true)}
                disabled={willExcuse.length === 0 || run.isPending}
                className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
              >
                {run.isPending ? 'Sending...' : `Let the studio know`}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}
